<app-loading *ngIf="contentReady"></app-loading>

<section class="bb-page">
  <div class="bb-page-shell">

    <!-- Header -->
    <header class="bb-page-header">
      <div class="bb-page-title-block">
        <span class="bb-eyebrow">{{ 'Manage Promo Codes' | transloco }}</span>
        <h2 class="bb-page-title">{{ 'Manage Promo Codes' | transloco }}</h2>
      </div>
    </header>

    <!-- Promo Code Form -->
    <form [formGroup]="promocodeForm" (ngSubmit)="addCode()">
      <div class="bb-card bb-p-20">
        <div class="bb-form-grid">

          @if(submitted && promocodeForm.invalid) {
          <div class="bb-form-alert bb-field-full" role="alert">
            {{ 'Review the promo code data before saving' | transloco }}

            @if(getMissingRequiredCount() > 0) {
            <span>
              ({{ getMissingRequiredCount() }} {{ 'required fields' | transloco }})
            </span>
            }
          </div>
          }

          <!-- Code -->
          <div class="bb-field">
            <label class="bb-label" for="code">{{ "Promo Code" | transloco }}</label>

            <input
              class="bb-input"
              id="code"
              type="text"
              formControlName="code"
              placeholder="{{ 'e.g. SUMMER25' | transloco }}"
              [ngClass]="{
                'is-invalid': promocodeForm.get('code')?.touched && promocodeForm.get('code')?.invalid,
                'is-valid': promocodeForm.get('code')?.touched && promocodeForm.get('code')?.valid
              }"
            />

            @if(shouldShowError('code')) {
            <div class="bb-field-error">
              {{ getErrorMessage('code', ('Promo Code' | transloco)) }}
            </div>
            }
          </div>

          <!-- Type -->
          <div class="bb-field">
            <label class="bb-label" for="type">{{ "Promo Code Type" | transloco }}</label>

            <div class="bb-select-wrap">
              <select
                class="bb-select"
                id="type"
                formControlName="type"
                [ngClass]="{
                  'is-invalid': promocodeForm.get('type')?.touched && promocodeForm.get('type')?.invalid,
                  'is-valid': promocodeForm.get('type')?.touched && promocodeForm.get('type')?.valid
                }"
              >
                <option value="0">{{ "free delivery" | transloco }}</option>
                <option value="1">{{ "discount percentage" | transloco }}</option>
              </select>
            </div>

            @if(shouldShowError('type')) {
            <div class="bb-field-error">
              {{ getErrorMessage('type', ('Promo Code Type' | transloco)) }}
            </div>
            }
          </div>

          <!-- Percentage -->
          <div class="bb-field">
            <label class="bb-label" for="percentage">{{ "Percentage" | transloco }}</label>

            <input
              class="bb-input"
              id="percentage"
              type="number"
              min="0"
              max="100"
              formControlName="percentage"
              placeholder="0–100"
              [ngClass]="{
                'is-invalid': promocodeForm.get('percentage')?.touched && promocodeForm.get('percentage')?.invalid,
                'is-valid': promocodeForm.get('percentage')?.touched && promocodeForm.get('percentage')?.valid
              }"
            />

            @if(shouldShowError('percentage')) {
            <div class="bb-field-error">
              {{ getErrorMessage('percentage', ('Percentage' | transloco)) }}
            </div>
            }
          </div>

          <!-- Expiration Date -->
          <div class="bb-field">
            <label class="bb-label" for="expiration_date">{{ "expiration date" | transloco }}</label>

            <div class="bb-booking-calendar-field" [dir]="isAr ? 'rtl' : 'ltr'">
              <button
                type="button"
                class="bb-booking-calendar-trigger"
                id="expiration_date"
                (click)="toggleExpirationCalendar()"
                [ngClass]="{
                  'is-invalid': promocodeForm.get('expiration_date')?.touched && promocodeForm.get('expiration_date')?.invalid,
                  'is-valid': promocodeForm.get('expiration_date')?.touched && promocodeForm.get('expiration_date')?.valid
                }"
              >
                <span>{{ getExpirationDateDisplay() }}</span>
                <i class="fa-solid fa-calendar-days"></i>
              </button>

              @if(showExpirationCalendar) {
              <div class="bb-booking-calendar-panel">
                <div class="bb-booking-calendar-head">
                  <button type="button" (click)="previousExpirationCalendarMonth()">
                    <i class="fa-solid" [ngClass]="isAr ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
                  </button>

                  <strong>{{ getExpirationCalendarTitle() }}</strong>

                  <button type="button" (click)="nextExpirationCalendarMonth()">
                    <i class="fa-solid" [ngClass]="isAr ? 'fa-chevron-left' : 'fa-chevron-right'"></i>
                  </button>
                </div>

                <div class="bb-booking-calendar-weekdays">
                  @for(dayName of getExpirationCalendarWeekDays(); track dayName) {
                  <span>{{ dayName }}</span>
                  }
                </div>

                <div class="bb-booking-calendar-grid">
                  @for(calendarDay of getExpirationCalendarDays(); track calendarDay.date.getTime()) {
                  <button
                    type="button"
                    class="bb-booking-calendar-day"
                    [class.other-month]="!calendarDay.isCurrentMonth"
                    [class.today]="calendarDay.isToday"
                    [class.selected]="calendarDay.isSelected"
                    [disabled]="calendarDay.isDisabled"
                    (click)="selectExpirationCalendarDate(calendarDay.date)"
                  >
                    {{ calendarDay.dayNumber }}
                  </button>
                  }
                </div>
              </div>
              }
            </div>

            <input type="hidden" formControlName="expiration_date" />

            @if(shouldShowError('expiration_date')) {
            <div class="bb-field-error">
              {{ getErrorMessage('expiration_date', ('expiration date' | transloco)) }}
            </div>
            }
          </div>

          <!-- Active Status -->
          <div class="bb-field">
            <label class="bb-label">{{ "Status" | transloco }}</label>

            <div class="bb-shift-option-row bb-m-0">
              <div>
                <strong>{{ "Active" | transloco }}</strong>
                <small>{{ 'Enable or disable this promo code' | transloco }}</small>
              </div>

              <label class="bb-switch">
                <input type="checkbox" id="active" formControlName="active" />
                <span></span>
              </label>
            </div>
          </div>

          <!-- Submit -->
          <div class="bb-field bb-flex bb-items-end bb-justify-end">
            <button
              class="bb-btn bb-btn-primary bb-btn-wide"
              type="submit"
              [class.is-disabled]="promocodeForm.invalid"
            >
              {{ "Submit" | transloco }}
            </button>
          </div>

        </div>
      </div>
    </form>

    <!-- Table Section -->
    <div class="bb-table-card bb-p-16 bb-mt-20">
      <label class="bb-label">{{ 'Promo Codes List' | transloco }}</label>

      <div class="bb-table-scroll">
        <table class="bb-table table">
          <thead>
            <tr>
              <th>{{ "Promo Code" | transloco }}</th>
              <th>{{ "Promo Code Type" | transloco }}</th>
              <th>{{ "Percentage" | transloco }}</th>
              <th>{{ "expiration date" | transloco }}</th>
              <th>{{ "Active" | transloco }}</th>
              <th class="bb-actions-th">{{ "Actions" | transloco }}</th>
            </tr>
          </thead>

          <tbody>
            <tr *ngFor="let element of dataSource.data">
              <td>
                <strong class="bb-text-navy bb-font-bold">{{ element.code }}</strong>
              </td>

              <td>
                <span *ngIf="element.type == 0" class="bb-badge bb-badge-soft-cyan">
                  {{ "free delivery" | transloco }}
                </span>

                <span *ngIf="element.type == 1" class="bb-badge bb-badge-soft-blue">
                  {{ "discount percentage" | transloco }}
                </span>
              </td>

              <td>
                <span *ngIf="element.percentage" class="bb-price bb-text-blue">
                  {{ element.percentage }}%
                </span>

                <span *ngIf="!element.percentage">-</span>
              </td>

              <td>
                <span *ngIf="element.expiration_date">
                  {{ element.expiration_date | date:'mediumDate' }}
                </span>

                <span *ngIf="!element.expiration_date">-</span>
              </td>

              <td>
                <span *ngIf="element.active" class="bb-badge bb-badge-success">
                  {{ "on" | transloco }}
                </span>

                <span *ngIf="!element.active" class="bb-badge bb-badge-danger">
                  {{ "off" | transloco }}
                </span>
              </td>

              <td class="bb-actions-td">
                <button
                  type="button"
                  class="bb-btn bb-btn-danger bb-btn-sm"
                  (click)="deleteItem(element.id)"
                >
                  {{ "Delete" | transloco }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- No Data State -->
      <div *ngIf="noData" class="bb-empty-state bb-text-center">
        <app-nodata></app-nodata>
      </div>
    </div>

  </div>
</section>